import { Box, Separator, Text, ToastAction, ToastType, useModalState, useToastsDispatch } from '@bechtel/gds';
import { CancelButtonStyled, FooterStyled, ReturnDetailContainerStyled, ReturnToolDetailBodyStyled } from './styled';
import { ReturnToolDetailProps } from './types';

import { useContext, useState } from 'react';
//@ts-ignore images ignore
import { useMutation } from '@apollo/client';
import { ToolCheckInInput, ToolCheckInResult, ToolInventory } from '@bechtel/ht-types';
import { useNavigate } from 'react-router';
import { ActionContent } from '~/components/action-content';
import { ConfirmDialog } from '~/components/confirm-dialog';
import { ErrorModal } from '~/components/molecules/error-modal';
import { TOOL_ORDER_CHECKINS } from '~/gql/tool-order-checkins';
//@ts-ignore svg images
import { i18next } from '@bechtel/ht-clients-common';
import { ReactComponent as EmptyResult } from '~/images/Empty_search-results.svg';
import { PrimaryButtonStyled } from '~/styles';
import { ErrorDescriptionMessage } from '../../../components/molecules/error-modal/types';
import { ReturnToolContext } from '../return-tool-context';
import { actionType } from '../types';
import { ReturnDetailItem } from './item';
import SearchToolByNameBarCode from './search-tools';

const ReturnToolDetail: React.FC<ReturnToolDetailProps> = ({ selectedUser, selectedToolRoomId, onClose }) => {
  const [cartErrorMessage, setCartErrorMessage] = useState<string>('');
  const [errorDetail, setErrorDetail] = useState<ErrorDescriptionMessage[]>([]);
  const navigate = useNavigate();
  const dispatchToast = useToastsDispatch();
  const { selectedCheckInToolList, selectedIds, updateData } = useContext(ReturnToolContext);
  const [toolsCheckInMutation, { loading: toolsCheckInMutationLoading }] = useMutation<
    { toolCheckIn: ToolCheckInResult },
    { toolCheckInInput: ToolCheckInInput }
  >(TOOL_ORDER_CHECKINS, { refetchQueries: ['SearchToolOrderInventoryByOrderedFor'] });

  const {
    open: openCancelOrderConfirmDialog,
    close: closeCancelOrderConfirmDialog,
    visible: visibleCancelOrderConfirmDialog,
  } = useModalState(false);

  const completeCheckIn = async () => {
    if (selectedIds && selectedIds.length > 0) {
      const payload: ToolCheckInInput = {
        recipientId: selectedUser?.id ?? '',
        toolCheckInItems: [...selectedIds],
        toolRoomId: (selectedCheckInToolList && selectedCheckInToolList[0].toolRoomId) ?? '',
      };
      await toolsCheckInMutation({
        variables: { toolCheckInInput: payload },
        onCompleted: (data) => {
          if (data.toolCheckIn?.toolCheckIn) {
            const toastContent: ToastAction = {
              type: 'ADD_TOAST',
              payload: {
                toastType: ToastType.SUCCESS,
                content: 'Tools Checkin Successfully',
                autoDismissTimeout: 5000,
              },
            };
            dispatchToast(toastContent);
            updateData(actionType.RESET_ALL, {});
            if (selectedToolRoomId && onClose) {
              onClose();
            } else {
              navigate(`/returns`, { state: { selectedUser: selectedUser } });
            }
          } else {
            let errors: ErrorDescriptionMessage[];
            if (data?.toolCheckIn?.errors?.length == 1 && data.toolCheckIn.errors[0]?.toolInventoryId == null) {
              setCartErrorMessage(
                data.toolCheckIn?.errors && data.toolCheckIn?.errors[0]?.error
                  ? i18next.t(data.toolCheckIn.errors[0].error)
                  : 'Error occured while Tools Checkin'
              );
            } else if (data?.toolCheckIn?.errors && data?.toolCheckIn?.errors.length > 0) {
              errors = selectedCheckInToolList?.flatMap((i) => {
                const matchingErrors = data.toolCheckIn.errors.filter((j) => i.id === j?.toolInventoryId);
                return matchingErrors.map((j) => ({
                  title: i18next.t(j?.error ?? ''),
                  description: i.description ?? '',
                }));
              });
              if (errors) setErrorDetail(errors);
            } else setCartErrorMessage('Error occured while Tools Checkin');
          }
        },
        onError: () => {
          setCartErrorMessage('Error occured while Tools Checkin');
        },
      });
    }
  };

  const closeModal = () => {
    setErrorDetail([]);
    setCartErrorMessage('');
  };

  const confirmCancel = () => {
    updateData(actionType.RESET_ALL, {});
    closeCancelOrderConfirmDialog();
    navigate(`/returns/new`, { state: { selectedUser: selectedUser } });
  };

  return (
    <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }} style={{ width: '100%' }}>
      <SearchToolByNameBarCode />
      <Separator spacing="000" color="grey.300" />

      {selectedCheckInToolList && selectedCheckInToolList.length > 0 ? (
        <ReturnDetailContainerStyled>
          {selectedCheckInToolList.map((item: ToolInventory, index) => {
            return <ReturnDetailItem index={index} toolInventory={item} key={item.id} />;
          })}{' '}
        </ReturnDetailContainerStyled>
      ) : (
        <ReturnToolDetailBodyStyled flexDirection={{ sm: 'column' }}>
          <Box>
            <EmptyResult />
          </Box>
          <Box mt={{ sm: '500' }}>
            <Text size="300" color="grey.600" family="OpenSansRegular">
              Search tools to view…
            </Text>
          </Box>
        </ReturnToolDetailBodyStyled>
      )}
      {selectedCheckInToolList && selectedCheckInToolList.length > 0 && (
        <FooterStyled>
          <CancelButtonStyled onClick={openCancelOrderConfirmDialog} disabled={toolsCheckInMutationLoading}>
            <Text color="grey.1000" size="300">
              Cancel
            </Text>
          </CancelButtonStyled>
          <PrimaryButtonStyled onClick={completeCheckIn} disabled={toolsCheckInMutationLoading}>
            <ActionContent icon="check" label={`Complete Check In (${selectedCheckInToolList.length})`} />
          </PrimaryButtonStyled>
        </FooterStyled>
      )}
      {errorDetail && errorDetail.length > 0 && (
        <ErrorModal
          close={closeModal}
          message={cartErrorMessage}
          title="Validation Message"
          closeText="close"
          errorDetails={errorDetail}
          minHeight="200px"
          maxHeight="400px"
        />
      )}
      {cartErrorMessage && (
        <ErrorModal
          close={closeModal}
          message={cartErrorMessage}
          title="Validation Message"
          closeText="close"
          minHeight="200px"
          maxHeight="400px"
        />
      )}

      {visibleCancelOrderConfirmDialog && (
        <ConfirmDialog
          message={'Are you sure you want to cancel this order?'}
          onCancel={closeCancelOrderConfirmDialog}
          title="Cancel Order"
          onConfirm={() => {
            if (selectedToolRoomId && onClose) {
              onClose();
            } else {
              confirmCancel();
            }
          }}
          cancelText="NO, LEAVE AS IS"
          confirmText="CONFIRM"
        ></ConfirmDialog>
      )}
    </Box>
  );
};

export default ReturnToolDetail;
